import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  Plus,
  Edit,
  Trash2,
  Tag,
  Video,
  Search,
  Eye,
  EyeOff
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import useConfirmDialog from '@/hooks/useConfirmDialog';

export default function BenefitsManagement() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    short_description: '',
    full_description: '',
    category_id: '',
    base_premium: '',
    coverage_amount: '',
    video_url: '',
    image_url: '',
    is_active: true,
    features: [],
    faq: []
  });
  const { confirmDialogProps, openConfirm } = useConfirmDialog();
  const [newFeature, setNewFeature] = useState('');
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setLoading(true);
    try {
      const user = await api.auth.me();
      const systemAdmin = user?.user_role === 'admin' || user?.role === 'ADMIN';
      setIsSystemAdmin(systemAdmin);

      if (systemAdmin) {
        const employersData = await api.entities.Employer.list();
        setEmployers(employersData);

        const defaultEmployerId = employersData?.[0]?.id || '';
        setSelectedEmployerId(defaultEmployerId);
        if (defaultEmployerId) {
          await loadData(defaultEmployerId);
        } else {
          setProducts([]);
          setCategories([]);
        }
      } else {
        await loadData();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (employerId) => {
    try {
      const usingEmployerId = employerId || undefined;
      const useTenantFilter = Boolean(usingEmployerId);

      const [productsData, categoriesData] = await Promise.all(
        useTenantFilter
          ? [
              api.entities.BenefitProduct.filter({ employer_id: usingEmployerId }),
              api.entities.BenefitCategory.filter({ employer_id: usingEmployerId })
            ]
          : [
              api.entities.BenefitProduct.list(),
              api.entities.BenefitCategory.list()
            ]
      );
      console.log('Loaded products:', productsData.map(p => ({ name: p.name, is_active: p.is_active })));
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEmployerChange = async (value) => {
    setSelectedEmployerId(value);
    setLoading(true);
    try {
      await loadData(value);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || 'Uncategorized';
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());

    let matchesVisibility = true;
    if (visibilityFilter === 'active') {
      matchesVisibility = product.is_active === true;
    } else if (visibilityFilter === 'inactive') {
      matchesVisibility = product.is_active === false;
    }

    return matchesSearch && matchesVisibility;
  });

  // Count products by status
  const activeCount = products.filter(p => p.is_active === true).length;
  const inactiveCount = products.filter(p => p.is_active === false).length;

  const openProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      
      // Parse JSON fields if they're strings
      let features = product.features || [];
      if (typeof features === 'string') {
        try {
          features = JSON.parse(features);
        } catch (e) {
          console.error('Failed to parse features:', e);
          features = [];
        }
      }
      
      let faq = product.faq || [];
      if (typeof faq === 'string') {
        try {
          faq = JSON.parse(faq);
        } catch (e) {
          console.error('Failed to parse faq:', e);
          faq = [];
        }
      }
      
      setProductForm({
        name: product.name || '',
        short_description: product.short_description || '',
        full_description: product.full_description || '',
        category_id: product.category_id || '',
        base_premium: product.base_premium || '',
        coverage_amount: product.coverage_amount || '',
        video_url: product.video_url || '',
        image_url: product.image_url || '',
        is_active: product.is_active !== false,
        features: features,
        faq: faq
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        name: '',
        short_description: '',
        full_description: '',
        category_id: '',
        base_premium: '',
        coverage_amount: '',
        video_url: '',
        image_url: '',
        is_active: true,
        features: [],
        faq: []
      });
    }
    setShowProductModal(true);
  };

  const handleSaveProduct = async () => {
    try {
      const data = {
        ...productForm,
        ...(isSystemAdmin && selectedEmployerId ? { employer_id: selectedEmployerId } : {}),
        base_premium: Number.parseFloat(productForm.base_premium) || 0,
        coverage_amount: Number.parseFloat(productForm.coverage_amount) || 0
      };

      if (editingProduct) {
        await api.entities.BenefitProduct.update(editingProduct.id, data);
        toast.success('Product updated successfully');
      } else {
        await api.entities.BenefitProduct.create(data);
        toast.success('Product created successfully');
      }

      setShowProductModal(false);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (product) => {
    try {
      await api.entities.BenefitProduct.delete(product.id);
      toast.success('Product deleted');
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete product');
    }
  };

  const confirmDeleteProduct = (product) => {
    openConfirm({
      title: 'Delete this benefit product?',
      description: `${product.name} will be removed from the catalog. Existing enrollments remain but the product will no longer be available for new enrollments unless you recreate it.`,
      confirmLabel: 'Delete product',
      isDestructive: true,
      onConfirm: () => handleDeleteProduct(product)
    });
  };

  const toggleProductActive = async (product) => {
    try {
      const newStatus = !product.is_active;
      console.log('Toggling product:', product.name, 'from', product.is_active, 'to', newStatus);
      
      const response = await api.entities.BenefitProduct.update(product.id, {
        is_active: newStatus
      });
      
      console.log('Update response:', response);
      toast.success(`Product ${newStatus ? 'activated' : 'deactivated'}`);
      await loadData();
    } catch (err) {
      console.error('Toggle error:', err);
      toast.error('Failed to update product status');
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setProductForm({
        ...productForm,
        features: [...productForm.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setProductForm({
      ...productForm,
      features: productForm.features.filter((_, i) => i !== index)
    });
  };

  const addFaq = () => {
    if (newFaq.question.trim() && newFaq.answer.trim()) {
      setProductForm({
        ...productForm,
        faq: [...productForm.faq, { ...newFaq }]
      });
      setNewFaq({ question: '', answer: '' });
    }
  };

  const removeFaq = (index) => {
    setProductForm({
      ...productForm,
      faq: productForm.faq.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Benefits Management</h1>
          <p className="text-slate-500 mt-1">
            Manage benefit products and packages
          </p>
        </div>
        <Button 
          onClick={() => openProductModal()}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Benefit Product
        </Button>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-4 space-y-4">
          {isSystemAdmin && (
            <div className="grid gap-2">
              <Label>Employer</Label>
              <Select value={selectedEmployerId} onValueChange={handleEmployerChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer" />
                </SelectTrigger>
                <SelectContent>
                  {employers.map((employer) => (
                    <SelectItem key={employer.id} value={employer.id}>
                      {employer.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Visibility Filter Tabs */}
          <div className="flex gap-2 border-b">
            <button
              onClick={() => setVisibilityFilter('all')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                visibilityFilter === 'all'
                  ? 'text-emerald-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              All Benefits ({products.length})
              {visibilityFilter === 'all' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" />
              )}
            </button>
            <button
              onClick={() => setVisibilityFilter('active')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                visibilityFilter === 'active'
                  ? 'text-emerald-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Eye className="w-4 h-4 inline mr-1" />
              Active Only ({activeCount})
              {visibilityFilter === 'active' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" />
              )}
            </button>
            <button
              onClick={() => setVisibilityFilter('inactive')}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                visibilityFilter === 'inactive'
                  ? 'text-emerald-600'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <EyeOff className="w-4 h-4 inline mr-1" />
              Inactive Only ({inactiveCount})
              {visibilityFilter === 'inactive' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600" />
              )}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Products Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.map((product, idx) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <Card className={`h-full ${product.is_active === false ? 'opacity-60' : ''}`}>
              <div className="relative h-40 overflow-hidden rounded-t-lg">
                <img
                  src={product.image_url || 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400'}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 right-3 flex gap-2">
                  {product.is_active === false && (
                    <Badge className="bg-slate-800 text-white">Inactive</Badge>
                  )}
                  {product.video_url && (
                    <Badge className="bg-red-500 text-white">
                      <Video className="w-3 h-3" />
                    </Badge>
                  )}
                </div>
              </div>
              
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">{product.name}</h3>
                    <Badge variant="outline" className="mt-1">
                      <Tag className="w-3 h-3 mr-1" />
                      {getCategoryName(product.category_id)}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-emerald-600">
                      ${Number.parseFloat(product.base_premium || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500">/month</p>
                  </div>
                </div>
                
                <p className="text-sm text-slate-600 line-clamp-2">
                  {product.short_description}
                </p>
                
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => openProductModal(product)}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleProductActive(product)}
                  >
                    {product.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => confirmDeleteProduct(product)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Edit Benefit Product' : 'Add Benefit Product'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
              <TabsTrigger value="faq">FAQ</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4 py-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Product Name</Label>
                  <Input
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select 
                    value={productForm.category_id}
                    onValueChange={(v) => setProductForm({ ...productForm, category_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Monthly Premium ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={productForm.base_premium}
                    onChange={(e) => setProductForm({ ...productForm, base_premium: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Coverage Amount ($)</Label>
                  <Input
                    type="number"
                    value={productForm.coverage_amount}
                    onChange={(e) => setProductForm({ ...productForm, coverage_amount: e.target.value })}
                  />
                </div>
                
                <div className="md:col-span-2 space-y-2">
                  <Label>Short Description</Label>
                  <Input
                    value={productForm.short_description}
                    onChange={(e) => setProductForm({ ...productForm, short_description: e.target.value })}
                    placeholder="Brief description for listings"
                  />
                </div>
                
                <div className="md:col-span-2 space-y-2">
                  <Label>Full Description</Label>
                  <Textarea
                    value={productForm.full_description}
                    onChange={(e) => setProductForm({ ...productForm, full_description: e.target.value })}
                    rows={4}
                    placeholder="Detailed product description"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Video URL (YouTube)</Label>
                  <Input
                    value={productForm.video_url}
                    onChange={(e) => setProductForm({ ...productForm, video_url: e.target.value })}
                    placeholder="https://youtube.com/embed/..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Image URL</Label>
                  <Input
                    value={productForm.image_url}
                    onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <Switch
                    checked={productForm.is_active}
                    onCheckedChange={(checked) => setProductForm({ ...productForm, is_active: checked })}
                  />
                  <Label>Active (visible to employees)</Label>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="features" className="space-y-4 py-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Add a feature..."
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                />
                <Button onClick={addFeature} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-2">
                {productForm.features.map((feature, idx) => (
                  <div key={feature} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-slate-700">{feature}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeFeature(idx)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="faq" className="space-y-4 py-4">
              <div className="space-y-2">
                <Input
                  placeholder="Question"
                  value={newFaq.question}
                  onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                />
                <Textarea
                  placeholder="Answer"
                  value={newFaq.answer}
                  onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                  rows={2}
                />
                <Button onClick={addFaq} variant="outline" className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Add FAQ
                </Button>
              </div>
              
              <div className="space-y-3">
                {productForm.faq.map((item, idx) => (
                  <div key={item.question} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-slate-900">{item.question}</p>
                        <p className="text-sm text-slate-600 mt-1">{item.answer}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFaq(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowProductModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveProduct} className="bg-emerald-500 hover:bg-emerald-600">
              {editingProduct ? 'Update Product' : 'Create Product'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}